import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextField, COLORS } from 'pvs-design-system';
import { Box } from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { FlatTreeNode } from 'types';
import TreeSelect from './TreeSelect';

/** DS TextField-styled trigger (border, label, rotating chevron) that opens a
 *  popover containing a hierarchical TreeSelect (expand/collapse, lazy-loaded
 *  nodes) — gives a tree picker the look of a DS Select without flattening it. */
interface TreeSelectFieldProps {
  id: string;
  label: string;
  required?: boolean;
  nodes: FlatTreeNode[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onLoadChildren: (nodeId: number) => Promise<void>;
  /** Hides this node and all its descendants from the tree — used on Edit
   *  pages to prevent selecting the current item as its own parent. */
  excludeId?: number | null;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  disabledPlaceholder?: string;
  error?: boolean;
  errorMessage?: string;
  helperText?: React.ReactNode;
}

const TreeSelectField: React.FC<TreeSelectFieldProps> = ({
  id, label, required, nodes, selectedId, onSelect, onLoadChildren, excludeId,
  placeholder = 'Select…', loading = false, disabled = false, disabledPlaceholder,
  error, errorMessage, helperText,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedLabel = nodes.find((n) => n.id === selectedId)?.name ?? '';
  const isDisabled = disabled || loading;

  // Stable callback so React.memo on TreeSelect can bail out when the tree hasn't changed.
  const handleSelect = useCallback((nodeId: number) => {
    onSelect(nodeId);
    setOpen(false);
  }, [onSelect]);

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        id={id}
        label={label}
        required={required}
        placeholder={loading ? 'Loading…' : disabled ? disabledPlaceholder : placeholder}
        size="medium"
        fullWidth
        value={selectedLabel}
        disabled={isDisabled}
        error={error}
        errorMessage={errorMessage}
        helperText={helperText}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <KeyboardArrowDown
              sx={{ color: COLORS.gray[400], transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
            />
          ),
        }}
        onClick={() => { if (!isDisabled) setOpen((prev) => !prev); }}
      />
      {open && !isDisabled && (
        <Box
          sx={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
            background: '#fff', border: `1px solid ${COLORS.gray[200]}`, borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto',
          }}
        >
          <TreeSelect
            variant="inline"
            nodes={nodes}
            selectedId={selectedId}
            onSelect={handleSelect}
            onLoadChildren={onLoadChildren}
            excludeId={excludeId}
            placeholder={placeholder}
          />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(TreeSelectField);
